import React from 'react'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import styled from 'styled-components'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { Box, Divider, Flex, Grid, Text, VerticalDivider } from '../../../core-components'
import { PositioningProps } from '../../types'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { useGroup } from '../../wayfinding/setting-label/hooks'
import { flattenChildren } from '../../helpers'

type Variant = 'regular' | 'nested'

type Props = {
  children: React.ReactNode
  label: React.ReactNode
  variant?: Variant
} & PositioningProps

export const InputGroup = ({
  label,
  children,
  variant = 'regular',
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'InputGroup')
  invariant(
    typeValidator(InputGroupLabel)(label),
    'InputGroup label must be an InputGroup.Label component'
  )
  const { labelProps, groupProps, descriptionProps } = useGroup()
  const hasManualRow = flattenChildren(children).some(typeValidator(InputGroupRow))
  return (
    <SettingLabelContainer
      label={label}
      isRequired={false}
      labelElement='div'
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      {...positioningProps}
    >
      <Box
        borderRadius={variant === 'regular' ? 10 : undefined}
        overflow='hidden'
        bg='bg/section'
        boxShadow={variant === 'regular' ? 'border/section' : undefined}
        {...groupProps}
      >
        {hasManualRow ? (
          flattenChildren(children).map((child, index) => {
            if (index === 0 || !child) {
              return child
            }
            return (
              <React.Fragment key={index}>
                <Divider />
                {child}
              </React.Fragment>
            )
          })
        ) : (
          <InputGroupRow>{children}</InputGroupRow>
        )}
      </Box>
    </SettingLabelContainer>
  )
}

const InputGroupLabel = (props: React.ComponentProps<typeof SettingLabel>) => {
  return <SettingLabel {...props} />
}

const InputGroupText = ({ children }: { children: string }) => (
  <StyledText textStyle='form-value' color='fg/disabled'>
    {children}
  </StyledText>
)

const StyledText = styled(Text)`
  display: flex;
  flex-grow: 1;
  align-items: center;
  height: ${({ theme }) => theme.space[9]};
  padding: 0 ${({ theme }) => theme.space[5]};
  background: ${({ theme }) => theme.colors['bg/disabled']};
`

const InputGroupRow = ({
  children,
  gridAutoColumns = 'minmax(0, 1fr)',
}: {
  children: React.ReactNode
  gridAutoColumns?: string
}) => {
  return (
    <Grid gridAutoColumns={gridAutoColumns} gridAutoFlow='column'>
      {flattenChildren(children).map((child, index) => {
        if (index === 0) {
          return child
        }
        return (
          <Flex key={index}>
            <VerticalDivider />
            {child}
          </Flex>
        )
      })}
    </Grid>
  )
}

InputGroup.Label = InputGroupLabel
InputGroup.Text = InputGroupText
InputGroup.Row = InputGroupRow
